/**
 * Shared helpers for the Clustering UI.
 */

import type { User } from '../../contexts/AuthContext';
import type { BadgeProps } from '../ui/Badge/Badge';
import type { BatchStatus } from '../../types/clustering';

/** Viewer-tier users can only read; everyone else can mutate. */
export function canMutate(user: User | null | undefined): boolean {
  return !!user && user.role !== 'viewer';
}

export interface StatusMeta {
  variant: NonNullable<BadgeProps['variant']>;
  label: string;
  isRunning: boolean;
}

export function statusMeta(status: BatchStatus): StatusMeta {
  switch (status) {
    case 'done':
      return { variant: 'success', label: 'Done', isRunning: false };
    case 'failed':
      return { variant: 'error', label: 'Failed', isRunning: false };
    case 'cancelled':
      return { variant: 'tertiary', label: 'Cancelled', isRunning: false };
    case 'awaiting_quota':
      return { variant: 'warning', label: 'Awaiting quota', isRunning: false };
    case 'queued':
    case 'pending':
      return { variant: 'info', label: 'Queued', isRunning: true };
    case 'estimating':
      return { variant: 'info', label: 'Estimating', isRunning: true };
    case 'fetching_serp':
      return { variant: 'warning', label: 'Fetching SERPs', isRunning: true };
    case 'enriching':
      return { variant: 'warning', label: 'Enriching', isRunning: true };
    case 'normalizing':
      return { variant: 'warning', label: 'Normalizing', isRunning: true };
    case 'clustering':
      return { variant: 'warning', label: 'Clustering', isRunning: true };
    case 'scoring':
      return { variant: 'warning', label: 'Scoring', isRunning: true };
    case 'labeling':
      return { variant: 'warning', label: 'Labeling', isRunning: true };
    case 'running':
      return { variant: 'warning', label: 'Running', isRunning: true };
    default:
      return { variant: 'secondary', label: status, isRunning: false };
  }
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString();
}

export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `$${value.toFixed(2)}`;
}
